/**
 * Component: Anchor Panel
 * Description:
 *
 * Usage:
 *   - Automatically initialises on page load.
 *
 * Notes:
 *   - Locks body scroll while panel is open
 *   - Allows swipe-down to close on mobile
 */

export function anchorPanel(elements) {
    if (!elements || !elements.length) return;

    const toggleLinks = Array.from(elements);
    const activeClass = 'toggle-link__target--visible';
    const SWIPE_THRESHOLD = 60;

    let touchStartY = 0;
    let touchEndY = 0;
    let scrollPosition = 0;

    /* ---------------------------------------------------------------------
     * Helpers
     * ------------------------------------------------------------------ */

    function isMobile() {
        return window.innerWidth < 600;
    }

    function hasOpenPanel() {
        return toggleLinks.some((link) => {
            const targetId = link.getAttribute('aria-controls');
            const target = document.getElementById(targetId);
            return target?.classList.contains(activeClass);
        });
    }

    function lockBodyScroll() {
        scrollPosition = window.pageYOffset;

        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollPosition}px`;
        document.body.style.left = '0';
        document.body.style.right = '0';
        document.body.style.width = '100%';
    }

    function unlockBodyScroll() {
        document.body.style.position = '';
        document.body.style.top = '';
        document.body.style.left = '';
        document.body.style.right = '';
        document.body.style.width = '';

        window.scrollTo(0, scrollPosition);
    }

    /* ---------------------------------------------------------------------
     * Core behaviour
     * ------------------------------------------------------------------ */

    function closeAllDropdowns() {
        toggleLinks.forEach((link) => {
            const targetId = link.getAttribute('aria-controls');
            const target = document.getElementById(targetId);

            if (!target) return;

            target.classList.remove(activeClass);
            link.setAttribute('aria-expanded', 'false');

            const childLinks = target.querySelectorAll('.anchor-nav__link');
            childLinks.forEach((childLink) => {
                childLink.removeEventListener('click', handleAnchorLinkClick);
            });
        });

        document.body.classList.remove('has-tint');
        unlockBodyScroll();

        touchStartY = 0;
        touchEndY = 0;
    }

    function toggleDropdown(event) {
        event.preventDefault();

        const link = event.currentTarget;
        const targetId = link.getAttribute('aria-controls');
        const target = document.getElementById(targetId);
        if (!target) return;

        const isExpanded = link.getAttribute('aria-expanded') === 'true';
        const childLinks = target.querySelectorAll('.anchor-nav__link');

        closeAllDropdowns();

        if (!isExpanded) {
            link.setAttribute('aria-expanded', 'true');
            target.classList.add(activeClass);
            target.focus();
            document.body.classList.add('has-tint');

            lockBodyScroll();

            childLinks.forEach((childLink) => {
                childLink.addEventListener('click', handleAnchorLinkClick);
            });
        }
    }

    function handleAnchorLinkClick() {
        closeAllDropdowns();
    }

    /* ---------------------------------------------------------------------
     * Event bindings
     * ------------------------------------------------------------------ */

    toggleLinks.forEach((link) => {
        link.addEventListener('click', toggleDropdown);
    });

    document.addEventListener('click', (event) => {
        if (!hasOpenPanel()) return;

        const clickedInside = toggleLinks.some((link) => {
            const target = document.getElementById(link.getAttribute('aria-controls'));
            return link.contains(event.target) || target?.contains(event.target);
        });

        if (!clickedInside) {
            closeAllDropdowns();
        }
    });

    /* ---------------------------------------------------------------------
     * Prevent background scrolling when open (iOS safe)
     * ------------------------------------------------------------------ */

    document.addEventListener(
        'touchmove',
        (event) => {
            if (!isMobile() || !hasOpenPanel()) return;
            event.preventDefault();
        },
        { passive: false }
    );

    /* ---------------------------------------------------------------------
     * Mobile swipe-down to close (below 600px only)
     * ------------------------------------------------------------------ */

    document.addEventListener(
        'touchstart',
        (event) => {
            if (!isMobile() || !hasOpenPanel()) return;
            touchStartY = event.touches[0].clientY;
        },
        { passive: true }
    );

    document.addEventListener(
        'touchend',
        (event) => {
            if (!isMobile() || !hasOpenPanel()) return;

            touchEndY = event.changedTouches[0].clientY;
            const deltaY = touchEndY - touchStartY;

            if (deltaY > SWIPE_THRESHOLD) {
                closeAllDropdowns();
            }
        },
        { passive: true }
    );
}
